import React, { useState, useEffect } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
// Simple error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <button onClick={() => window.location.reload()}>Reload page</button>
        </div>
      );
    }

    return this.props.children;
  }
}
import { IonReactRouter } from '@ionic/react-router';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './theme/theme';
import GlobalStyles from './theme/globalStyles';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

/* i18n configuration */
import './i18n/config';

/* Pages */
import LanguageSelection from './pages/LanguageSelection';
import Auth from './pages/Auth';
import Register from './pages/Register';
import RegisterForm from './pages/RegisterForm';
import Home from './pages/Home';
import EmailVerification from './pages/EmailVerification';
import { NewPost } from './pages/NewPost';
import { Profile } from './pages/Profile';
import { Messages } from './pages/Messages';
import { PostView } from './pages/PostView';
import { EditPost } from './pages/EditPost';
import { Settings } from './pages/Settings';
import NotificationsPage from './pages/Notifications';
import Terms from './pages/Terms';
import { Search } from './pages/Search';
/* Components */
import { Navbar } from './components/Navbar';
import TutorialModal from './components/TutorialModal';

/* Auth Provider */
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { auth } from './firebase/config';

/* Services */
import NotificationService from './services/NotificationService';

/* Styles */
import './index.css';

setupIonicReact({
  animated: false,
  mode: 'md',  // Use Material Design mode consistently
  hardwareBackButton: false  // Disable hardware back button for web
});

const AuthenticatedApp: React.FC = () => {
  React.useEffect(() => {
    // Initialize push notifications
    const notificationService = NotificationService.getInstance();
    notificationService.initialize().catch(console.error);
  }, []);

  return (
    <>
      <IonRouterOutlet animation={null}>
        <Route exact path="/home">
          <Home />
        </Route>
        <Route exact path="/messages">
          <Messages />
        </Route>
        <Route exact path="/new-post">
          <NewPost />
        </Route>
        <Route exact path="/post/:id">
          <PostView />
        </Route>
        <Route exact path="/edit-post/:id">
          <EditPost />
        </Route>
        <Route exact path="/search">
          <Search />
        </Route>
        <Route exact path={["/profile/:id", "/my-profile"]}>
          <Profile />
        </Route>
        <Route exact path="/settings">
          <Settings />
        </Route>
        <Route exact path="/notifications">
          <NotificationsPage />
        </Route>
        <Route exact path="/">
          <Redirect to="/home" />
        </Route>
        <Route>
          <Redirect to="/home" />
        </Route>
      </IonRouterOutlet>
      <Navbar />
    </>
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <IonRouterOutlet animation={null}>
        <Route exact path="/auth">
          <Auth />
        </Route>
        <Route exact path="/register">
          <Register />
        </Route>
        <Route exact path="/register/:type">
          <RegisterForm />
        </Route>
        <Route exact path="/terms">
          <Terms />
        </Route>
        <Route exact path="/">
          <Redirect to="/auth" />
        </Route>
        <Route>
          <Redirect to="/auth" />
        </Route>
      </IonRouterOutlet>
    );
  }

  // User is authenticated but email not verified
  if (isAuthenticated && !auth.currentUser?.emailVerified) {
    return (
      <IonRouterOutlet animation={null}>
        <Route exact path="/email-verification">
          <EmailVerification />
        </Route>
        <Route exact path="/auth">
          <Redirect to="/email-verification" />
        </Route>
        <Route exact path="/language">
          <Redirect to="/email-verification" />
        </Route>
        <Route>
          <Redirect to="/email-verification" />
        </Route>
      </IonRouterOutlet>
    );
  }

  return <AuthenticatedApp />;
};

const App: React.FC = () => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [languageSelected, setLanguageSelected] = useState(false);
  
  // Check if this is the first time the user is opening the app
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    const hasSelectedLanguage = localStorage.getItem('hasSelectedLanguage');
    
    if (hasSelectedLanguage) {
      setLanguageSelected(true);
      if (!hasSeenTutorial) {
        setShowTutorial(true);
      }
    }
  }, []);
  
  const handleCloseTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem('hasSeenTutorial', 'true');
    // Redirect to auth page after closing the tutorial
    window.location.href = '/auth';
  };

  const handleLanguageSelected = () => {
    setLanguageSelected(true);
    localStorage.setItem('hasSelectedLanguage', 'true');
    
    // Show tutorial after language selection if user hasn't seen it
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    } else {
      // If user has seen tutorial, redirect to auth page
      window.location.href = '/auth';
    }
  };

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        <AuthProvider>
          <IonApp>
            {!languageSelected ? (
              <LanguageSelection onLanguageSelected={handleLanguageSelected} />
            ) : (
              <>
                <TutorialModal isOpen={showTutorial} onClose={handleCloseTutorial} />
                <IonReactRouter>
                  <AppRoutes />
                </IonReactRouter>
              </>
            )}
          </IonApp>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
